class MegaNavigation {
  constructor() {
    this.container = document.querySelector(".header__inner");
    this.nav = this.container?.querySelector(".nav");
    if (!this.container || !this.nav) return;

    this.triggers = this.nav.querySelectorAll("[data-nav-trigger]");
    this.activeHandle = null;
    this.closeTimeout = null;

    this.bindEvents();

    this.mobileNav = document.getElementById("mobile-nav");
    this.mobileToggle = document.querySelector(".mobile-toggle");
    this.isMobileOpen = false;

    this.initMobile();
  }

  bindEvents() {
    this.nav.addEventListener("click", (e) => {
      const trigger = e.target.closest("[data-nav-trigger]");
      if (!trigger) return;

      e.preventDefault();
      const handle = trigger.dataset.navTrigger;
      this.toggle(handle);
    });

    if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
      this.nav.addEventListener("mouseover", (e) => {
        if (this.isMobileOpen) return;

        const navItem = e.target.closest(".nav__item");
        if (!navItem || !this.nav.contains(navItem)) return;

        this.clearCloseTimeout();

        const trigger = navItem.querySelector("[data-nav-trigger]");
        const handle = trigger?.dataset.navTrigger;
        const panel = handle
          ? document.getElementById(`panel-${handle}`)
          : null;

        if (panel) {
          this.open(handle);
        } else {
          this.closeAll();
        }
      });

      const panelsContainer = this.container.querySelector(".nav-panels");

      panelsContainer?.addEventListener("mouseenter", () =>
        this.clearCloseTimeout(),
      );

      panelsContainer?.addEventListener("mouseleave", () =>
        this.startCloseTimeout(),
      );

      this.container.addEventListener("mouseleave", () =>
        this.startCloseTimeout(),
      );
    }

    document.addEventListener("click", (e) => {
      if (!this.container.contains(e.target)) {
        this.closeAll();
      }
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") {
        if (this.isMobileOpen) {
          this.closeMobile();
        } else {
          this.closeAll();
        }
      }
    });
  }

  toggle(handle) {
    if (this.activeHandle === handle) {
      this.closeAll();
      return;
    }

    this.open(handle);
  }

  open(handle) {
    if (this.activeHandle === handle) return;

    const previousHandle = this.activeHandle;
    const previousPanel = previousHandle
      ? document.getElementById(`panel-${previousHandle}`)
      : null;

    const previousTrigger = previousHandle
      ? this.nav.querySelector(`[data-nav-trigger="${previousHandle}"]`)
      : null;

    const trigger = this.nav.querySelector(`[data-nav-trigger="${handle}"]`);
    const panel = document.getElementById(`panel-${handle}`);

    if (!panel || !trigger) return;

    panel.classList.add("is-active");
    trigger.setAttribute("aria-expanded", "true");

    if (previousPanel) {
      setTimeout(() => {
        previousPanel.classList.remove("is-active");
      }, 120);
    }

    if (previousTrigger) {
      previousTrigger.setAttribute("aria-expanded", "false");
    }

    this.activeHandle = handle;
    document.body.classList.add("nav-active");
  }

  closeAll() {
    this.triggers.forEach((trigger) => {
      const handle = trigger.dataset.navTrigger;
      const panel = document.getElementById(`panel-${handle}`);

      trigger.setAttribute("aria-expanded", "false");
      panel?.classList.remove("is-active");
    });

    this.activeHandle = null;
    document.body.classList.remove("nav-active");
  }

  startCloseTimeout() {
    this.closeTimeout = setTimeout(() => {
      this.closeAll();
    }, 120);
  }

  clearCloseTimeout() {
    if (this.closeTimeout) {
      clearTimeout(this.closeTimeout);
      this.closeTimeout = null;
    }
  }

  initMobile() {
    if (!this.mobileNav || !this.mobileToggle) return;

    const rootLevel = this.mobileNav.querySelector(".mobile-nav__level--root");
    const triggers = this.mobileNav.querySelectorAll("[data-mobile-trigger]");
    const backBtn = this.mobileNav.querySelector(".mobile-nav__back");
    const closeBtn = this.mobileNav.querySelector(".mobile-nav__close");

    let activePanel = null;

    this.mobileToggle.addEventListener("click", () => {
      this.mobileNav.hidden = false;

      requestAnimationFrame(() => {
        this.mobileNav.classList.add("is-active");
      });

      this.mobileToggle.setAttribute("aria-expanded", "true");
      document.body.classList.add("nav-lock");
      this.isMobileOpen = true;

      backBtn.classList.remove("is-visible");
    });

    triggers.forEach((trigger) => {
      trigger.addEventListener("click", () => {
        const handle = trigger.dataset.mobileTrigger;
        const targetPanel = this.mobileNav.querySelector(
          `[data-mobile-panel="${handle}"]`,
        );

        if (!targetPanel) return;

        rootLevel.classList.add("is-leaving");
        targetPanel.classList.add("is-active");
        activePanel = targetPanel;

        backBtn.classList.add("is-visible");
      });
    });

    backBtn.addEventListener("click", () => {
      if (!activePanel) return;

      rootLevel.classList.remove("is-leaving");
      activePanel.classList.remove("is-active");
      activePanel = null;

      backBtn.classList.remove("is-visible");
    });

    closeBtn?.addEventListener("click", () => this.closeMobile());
  }

  closeMobile() {
    if (!this.mobileNav) return;

    const rootLevel = this.mobileNav.querySelector(".mobile-nav__level--root");
    const panels = this.mobileNav.querySelectorAll("[data-mobile-panel]");
    const backBtn = this.mobileNav.querySelector(".mobile-nav__back");

    this.mobileNav.classList.remove("is-active");
    this.mobileToggle.setAttribute("aria-expanded", "false");
    document.body.classList.remove("nav-lock");

    rootLevel.classList.remove("is-leaving");
    panels.forEach((panel) => panel.classList.remove("is-active"));

    backBtn.classList.remove("is-visible");

    setTimeout(() => {
      this.mobileNav.hidden = true;
    }, 350);

    this.isMobileOpen = false;
  }
}

class HeaderSearch {
  constructor() {
    this.abortController = null;

    this.header = document.querySelector(".header");
    this.openButtons = document.querySelectorAll(".header__search-button");
    this.closeButtons = document.querySelectorAll(".header__search-close");

    this.init();
  }

  init() {
    this.inputs = document.querySelectorAll(".header__search-input");
    this.resultsContainers = document.querySelectorAll(
      "[data-predictive-search]",
    );

    this.bindEvents();
  }

  bindEvents() {
    this.openButtons.forEach((btn) => {
      btn.addEventListener("click", () => this.open());
    });

    this.closeButtons.forEach((btn) => {
      btn.addEventListener("click", () => this.close());
    });

    let timeout;

    this.inputs.forEach((input) => {
      input.addEventListener("input", (e) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => this.handleInput(e), 200);
      });
    });

    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });
  }

  open() {
    this.header.classList.add("active-search");

    const input = this.header.querySelector(".header__search-input");

    requestAnimationFrame(() => {
      input?.focus();
    });
  }

  close() {
    this.header.classList.remove("active-search");

    const input = this.header.querySelector(".header__search-input");
    input?.blur();

    this.clearResults();
  }

  async handleInput(e) {
    const query = e.target.value.trim();

    if (query.length < 2) {
      this.clearResults();
      return;
    }

    if (this.abortController) {
      this.abortController.abort();
    }

    this.abortController = new AbortController();

    try {
      const searchUrl = document.body.dataset.searchUrl;

      const response = await fetch(
        `${searchUrl}/suggest?q=${encodeURIComponent(
          query,
        )}&section_id=predictive-search&resources[type]=product&resources[limit]=20`,
        { signal: this.abortController.signal },
      );

      const html = await response.text();
      this.renderResults(html);
    } catch (err) {
      if (err.name !== "AbortError") {
        console.error("Predictive search error:", err);
      }
    }
  }

  renderResults(html) {
    if (!this.resultsContainers.length) return;

    this.resultsContainers.forEach((container) => {
      container.innerHTML = html;
    });
  }

  clearResults() {
    if (!this.resultsContainers.length) return;

    this.resultsContainers.forEach((container) => {
      container.innerHTML = "";
    });
  }
}

class HeaderScroll {
  constructor() {
    this.header = document.querySelector(".header");
    this.announcementBar = document.querySelector(".announcement-bar");

    if (!this.header) return;

    this.lastScroll = 0;
    this.ticking = false;

    this.bindEvents();
  }

  bindEvents() {
    window.addEventListener("scroll", () => {
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          this.ticking = false;
        });

        this.ticking = true;
      }
    });
  }

  handleScroll() {
    const currentScroll = window.scrollY;
    const shippingBar = document.querySelector(".ge-free-shipping-container");

    if (currentScroll <= 0) {
      this.header.classList.remove("header--hidden");
      this.announcementBar?.classList.remove("announcement-bar--hidden");
      shippingBar?.classList.remove("shipping-bar--hidden");
      this.lastScroll = currentScroll;
      return;
    }

    if (currentScroll > this.lastScroll && currentScroll > 150) {
      this.header.classList.add("header--hidden");
      this.announcementBar?.classList.add("announcement-bar--hidden");
      shippingBar?.classList.add("shipping-bar--hidden");
    } else {
      this.header.classList.remove("header--hidden");
      this.announcementBar?.classList.remove("announcement-bar--hidden");
      shippingBar?.classList.remove("shipping-bar--hidden");
    }

    this.lastScroll = currentScroll;
  }
}

document.addEventListener("DOMContentLoaded", () => {
  new MegaNavigation();
  new HeaderSearch();
  new HeaderScroll();
});
